import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Sparkles, Wrench, Zap, Hammer, Paintbrush, Flower, Car,
  Home, Scissors, GraduationCap, PartyPopper, Laptop, Package, Heart,
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const iconMap = {
  Sparkles, Wrench, Zap, Hammer, Paintbrush, Flower, Car, Refrigerator: Wrench,
  Home, Scissors, GraduationCap, PartyPopper, Laptop, Package, Heart,
};

const CatalogPage = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const normalizeCategories = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.categories)) return payload.categories;
    return [];
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/public/services-catalog`);
        setCategories(normalizeCategories(response.data));
      } catch (error) {
        console.error('Error fetching categories', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const resolveName = (nameObj) => nameObj?.[i18n.language] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

  const Icon = ({ category }) => {
    const iconKey = category.icon_slug || category.icon || 'Sparkles';
    const IconComponent = iconMap[iconKey] || Sparkles;
    return <IconComponent className="w-12 h-12" />;
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
            {t('home.categories_title')}
          </h1>
          <p className="text-xl text-muted-foreground">
            Browse professional services and connect with verified providers
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : categories.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No services available yet. Check back soon!
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  className="p-6 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`category-card-${category.id}`}
                  onClick={() => navigate(`/catalog/${category.slug || category.id}`)}
                >
                  <CardContent className="flex flex-col items-center text-center space-y-4 p-0">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: 'hsl(215 100% 95%)' }}>
                      <Icon category={category} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{resolveName(category.name)}</h3>
                      {category.provider_count && (
                        <Badge variant="secondary">{category.provider_count} {t('providers')}</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
  );
};

export default CatalogPage;
